/**
 * Price-chart geometry (F12, plan §1.6 · §3.3).
 *
 * A component receives coordinates and never computes them, so the line/area
 * paths, the grid and the label anchors are built here — pure, no DOM,
 * property-testable — rather than in the React tree that draws them.
 *
 * Port of the demo's `path(arr, w, h, pad)`, with one deliberate divergence: a
 * zero-span series is centred rather than scaled. Scaling it normally pins every
 * point to y = h - pad, which is exactly the area path's closing edge, so a flat
 * (or single-bar) series drew a line indistinguishable from the baseline.
 */
import {
  CHART_FIRST_SESSION_X,
  CHART_GRID_LINES,
  CHART_HEIGHT,
  CHART_INTERIOR_TICK_DY,
  CHART_LABEL_HIGH_Y,
  CHART_LABEL_INSET_X,
  CHART_LABEL_LOW_Y_OFFSET,
  CHART_MAX_POINTS,
  CHART_PAD,
  CHART_PRECISION,
  CHART_WIDTH,
} from '../constants/chart';

export interface ChartGridLine {
  readonly key: string;
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

/** Label anchor points, so the component reads coordinates rather than deriving them. */
export interface ChartLabelAnchors {
  readonly leftX: number;
  readonly rightX: number;
  readonly highY: number;
  readonly lowY: number;
  readonly firstSessionX: number;
  readonly interiorTickDy: number;
}

/** Inclusive high/low of a price series — the y-scale for path and hit-test. */
export interface ChartRange {
  readonly lo: number;
  readonly hi: number;
}

export interface ChartGeometry {
  readonly viewBox: string;
  readonly line: string;
  readonly area: string;
  readonly high: number;
  readonly low: number;
  /** Same object as `{ lo: low, hi: high }` — hit-test y-scale, stable for the geometry. */
  readonly range: ChartRange;
  readonly pointCount: number;
  readonly grid: readonly ChartGridLine[];
  readonly bottomY: number;
  readonly labels: ChartLabelAnchors;
}

/**
 * `first` is passed in rather than re-read: the caller has already destructured
 * it to prove the array is non-empty, so re-indexing here would reintroduce an
 * `undefined` branch that local control flow can never take (style §4 — state
 * the invariant, do not guard it).
 */
function endpointsOnly(bars: readonly number[], first: number): readonly number[] {
  const last = bars.reduce((_unused, v) => v, first);
  return first === last ? [first] : [first, last];
}

export function sampleEvenlyIndices(length: number, maxPoints: number): readonly number[] {
  const lastIdx = length - 1;
  return Array.from({ length: maxPoints }, (_unused, i) =>
    i === maxPoints - 1 ? lastIdx : Math.round((i / (maxPoints - 1)) * lastIdx),
  );
}

/** `noUncheckedIndexedAccess` fallback; callers pass in-range indices. */
function at(arr: readonly number[], i: number, fallback: number): number {
  return arr[i] ?? fallback;
}

function sampledVertices(
  bars: readonly number[],
  maxPoints: number,
): { readonly values: readonly number[]; readonly indices: readonly number[] } {
  const [first] = bars;
  if (first === undefined) return { values: bars, indices: [] };
  if (maxPoints <= 0 || bars.length <= maxPoints) {
    return {
      values: bars,
      indices: Array.from({ length: bars.length }, (_unused, i) => i),
    };
  }
  if (maxPoints === 1) {
    const values = endpointsOnly(bars, first);
    return { values, indices: values.length === 1 ? [0] : [0, bars.length - 1] };
  }
  const indices = sampleEvenlyIndices(bars.length, maxPoints);
  return {
    values: indices.map((idx) => at(bars, idx, first)),
    indices,
  };
}

/**
 * Evenly sample while preserving endpoints. Pass-through when under the max.
 * `maxPoints <= 0` means no decimation; `maxPoints === 1` keeps first+last.
 */
export function decimateBars(bars: readonly number[], maxPoints: number): readonly number[] {
  return sampledVertices(bars, maxPoints).values;
}

/**
 * Inclusive high/low over a non-empty series. The caller supplies `seed`
 * (already destructured as `bars[0]`) so this does not re-introduce an
 * undefined branch (style §4). Two scalar reduces — not a `{lo,hi}` per bar.
 */
function chartRange(bars: readonly number[], seed: number): ChartRange {
  return {
    lo: bars.reduce((acc, v) => (v < acc ? v : acc), seed),
    hi: bars.reduce((acc, v) => (v > acc ? v : acc), seed),
  };
}

/**
 * Inclusive high/low over finite bars only. An empty series or one with no
 * finite print is missing, not a scale of zero (R2b). A hole inside an
 * otherwise finite series is skipped so a trailing bad vendor bar cannot
 * blank the chart.
 */
export function chartScale(bars: readonly number[]): ChartRange | null {
  const finite = bars.filter((b) => Number.isFinite(b));
  const [seed] = finite;
  if (seed === undefined) return null;
  return chartRange(finite, seed);
}

/**
 * Plot y for a price on the same scale as `buildChartGeometry` (full-series
 * high/low, padded 900×300). A zero span centres on the plot, matching pathD.
 */
export function chartYAt(price: number, range: ChartRange): number {
  const plotH = CHART_HEIGHT - CHART_PAD * 2;
  const midY = CHART_PAD + plotH / 2;
  const span = range.hi - range.lo;
  if (span === 0) return midY;
  return CHART_PAD + (1 - (price - range.lo) / span) * plotH;
}

/**
 * Plot x for bar `index` of `count` (> 1). A single bar does not use this
 * scale: the path draws a segment, the pointer follows the cursor, keyboard
 * sits at mid-width.
 */
export function chartXAt(index: number, count: number): number {
  return (index / (count - 1)) * CHART_WIDTH;
}

/* eslint-disable functional/no-let, functional/no-loop-statements, functional/immutable-data */
function pathFromIndices(
  bars: readonly number[],
  indices: readonly number[],
  range: ChartRange,
): string {
  const n = bars.length;
  const parts: string[] = [];
  let drawing = false;
  for (const idx of indices) {
    const v = bars[idx];
    if (v === undefined || !Number.isFinite(v)) {
      drawing = false;
      continue;
    }
    const x = n <= 1 ? 0 : chartXAt(idx, n);
    const y = chartYAt(v, range);
    parts.push(`${drawing ? 'L' : 'M'}${x.toFixed(CHART_PRECISION)} ${y.toFixed(CHART_PRECISION)}`);
    drawing = true;
  }
  /* A lone moveto renders nothing: widen a single bar into a drawable segment. */
  if (parts.length === 1) {
    parts.push(
      `L${CHART_WIDTH.toFixed(CHART_PRECISION)} ${chartYAt(range.lo, range).toFixed(CHART_PRECISION)}`,
    );
  }
  return parts.join('');
}
/* eslint-enable functional/no-let, functional/no-loop-statements, functional/immutable-data */

/** Fixed grid — constant for CHART_HEIGHT / CHART_PAD; shared across geometry builds. */
const CHART_GRID: readonly ChartGridLine[] = Array.from(
  { length: CHART_GRID_LINES },
  (_unused, i) => {
    const y = CHART_PAD + i * ((CHART_HEIGHT - CHART_PAD * 2) / (CHART_GRID_LINES - 1));
    return { key: `g-${String(i)}`, x1: 0, y1: y, x2: CHART_WIDTH, y2: y };
  },
);

const CHART_LABEL_ANCHORS: ChartLabelAnchors = {
  leftX: CHART_LABEL_INSET_X,
  rightX: CHART_WIDTH - CHART_LABEL_INSET_X,
  highY: CHART_LABEL_HIGH_Y,
  lowY: CHART_HEIGHT - CHART_LABEL_LOW_Y_OFFSET,
  firstSessionX: CHART_FIRST_SESSION_X,
  interiorTickDy: CHART_INTERIOR_TICK_DY,
};

/** `viewBox` attribute for the SVG that draws a `buildChartGeometry` result. */
export const CHART_VIEWBOX = `0 0 ${String(CHART_WIDTH)} ${String(CHART_HEIGHT)}`;

/**
 * Build chart paths from a complete series. High/low (and the y-scale) use the
 * full finite series; decimation only thins path vertices for long windows, so
 * a spike that even sampling skips still sets the labelled high.
 *
 * Returns `null` when no finite bar exists — a missing price is not a price
 * of zero (R2b). A hole inside the series gaps the path (new moveto), the
 * same rule as overlay lines.
 */
export function buildChartGeometry(
  bars: readonly number[],
  maxPoints: number = CHART_MAX_POINTS,
): ChartGeometry | null {
  const range = chartScale(bars);
  if (range === null) return null;

  const sampled = sampledVertices(bars, maxPoints);
  const line = pathFromIndices(bars, sampled.indices, range);
  const bottomY = CHART_HEIGHT - CHART_PAD;
  const area = `${line}L${String(CHART_WIDTH)} ${String(bottomY)}L0 ${String(bottomY)}Z`;

  return {
    viewBox: CHART_VIEWBOX,
    line,
    area,
    high: range.hi,
    low: range.lo,
    range,
    /* The FULL series, not the decimated one: the bar count the user reads must
       describe the data, not how many vertices survived thinning. */
    pointCount: bars.length,
    grid: CHART_GRID,
    bottomY,
    labels: CHART_LABEL_ANCHORS,
  };
}
